import { useState } from 'react';
import { trpc } from '../lib/trpc';
import type { EntityType } from '@wineryxplorer/shared';

interface UploadOptions {
  entityType: EntityType;
  entityId: string;
  onSuccess?: (publicId: string, url: string) => void;
  onError?: (error: Error) => void;
  /** Called with 0-100 as the upload advances. Fires on every XHR progress event. */
  onProgress?: (progress: number) => void;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  width: number;
  height: number;
  bytes: number;
}

/**
 * Percentage of the overall progress bar reserved for each phase. Signing and
 * the metadata save are quick but not instant, so the bytes-on-the-wire phase
 * owns the middle band rather than the whole range.
 */
const SIGNATURE_DONE_PROGRESS = 10;
const UPLOAD_DONE_PROGRESS = 90;

/**
 * Uploads a file to Cloudinary via XMLHttpRequest so that real byte-level
 * progress is observable. `fetch` gives no upload progress events, which is why
 * this is not written with the rest of the app's HTTP calls.
 */
const uploadToCloudinary = (
  file: File,
  signature: {
    cloudName: string;
    apiKey: string;
    timestamp: number;
    signature: string;
    folder: string;
    context?: string | null;
  },
  onProgress: (fraction: number) => void
): Promise<CloudinaryUploadResult> =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('timestamp', signature.timestamp.toString());
    formData.append('signature', signature.signature);
    formData.append('api_key', signature.apiKey);
    formData.append('folder', signature.folder);
    if (signature.context) {
      formData.append('context', signature.context);
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${signature.cloudName}/image/upload`);

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error('Failed to upload to Cloudinary'));
        return;
      }

      try {
        resolve(JSON.parse(xhr.responseText) as CloudinaryUploadResult);
      } catch {
        reject(new Error('Cloudinary returned an unreadable response'));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Failed to upload to Cloudinary')));
    xhr.addEventListener('abort', () => reject(new Error('Upload was cancelled')));
    xhr.addEventListener('timeout', () => reject(new Error('Upload timed out')));

    xhr.send(formData);
  });

/**
 * Hook for uploading images to Cloudinary
 * Handles the entire upload flow:
 * 1. Get signed upload parameters from API
 * 2. Upload directly to Cloudinary
 * 3. Save metadata to database
 */
export const useImageUpload = () => {
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    error: null,
  });

  const getSignature = trpc.media.getUploadSignature.useMutation();
  const saveMedia = trpc.media.create.useMutation();

  const uploadImage = async (file: File, options: UploadOptions) => {
    const reportProgress = (progress: number) => {
      setState((prev) => ({ ...prev, progress }));
      options.onProgress?.(progress);
    };

    setState({ isUploading: true, progress: 0, error: null });
    options.onProgress?.(0);

    try {
      // Step 1: Get signed upload parameters
      const signature = await getSignature.mutateAsync({
        entityType: options.entityType,
        entityId: options.entityId,
      });

      reportProgress(SIGNATURE_DONE_PROGRESS);

      // Step 2: Upload directly to Cloudinary, reporting real byte progress
      const uploadData = await uploadToCloudinary(file, signature, (fraction) => {
        reportProgress(
          Math.round(
            SIGNATURE_DONE_PROGRESS + fraction * (UPLOAD_DONE_PROGRESS - SIGNATURE_DONE_PROGRESS)
          )
        );
      });

      reportProgress(UPLOAD_DONE_PROGRESS);

      // Step 3: Save metadata to database
      const savedMedia = await saveMedia.mutateAsync({
        entityType: options.entityType,
        entityId: options.entityId,
        cloudinaryPublicId: uploadData.public_id,
        cloudinaryUrl: uploadData.secure_url,
        filename: file.name,
        mimeType: file.type,
        width: uploadData.width,
        height: uploadData.height,
        sizeBytes: uploadData.bytes,
      });

      setState({ isUploading: false, progress: 100, error: null });
      options.onProgress?.(100);

      if (options.onSuccess) {
        options.onSuccess(uploadData.public_id, uploadData.secure_url);
      }

      return {
        publicId: uploadData.public_id,
        url: uploadData.secure_url,
        mediaId: savedMedia.id,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setState({ isUploading: false, progress: 0, error: errorMessage });

      if (options.onError) {
        options.onError(error instanceof Error ? error : new Error(errorMessage));
      }

      throw error;
    }
  };

  return {
    uploadImage,
    ...state,
  };
};
